import type { Node } from "@react-types/shared";
import { type FC, type ReactNode, useRef } from "react";
import { type AriaTabListProps, type AriaTabPanelProps, mergeProps, useTab, useTabList, useTabPanel } from "react-aria";
import { type TabListState, useTabListState } from "react-stately";

import { useTooltip } from "../../../hooks.ts";
import { useDataAttributes } from "../../../hooks/useDataAttributes.ts";
import { HStack } from "../../Stack/HStack.tsx";
import { Spacer } from "../../Stack/Spacer.tsx";
import { VStack } from "../../Stack/VStack.tsx";
import { AnimatedTooltip } from "../../Tooltip/AnimatedTooltip.tsx";
import style from "../TabPanel.module.css";
import type { Tab as TabType } from "../types.ts";

interface PanelTabsProps extends AriaTabListProps<TabType> {
  tabsActions?: ReactNode;
}

export const PanelTabs: FC<PanelTabsProps> = (props) => {
  const state = useTabListState(props);
  const ref = useRef(null);
  const { tabListProps } = useTabList(props, state, ref);

  const dataAttributes = useDataAttributes(props);

  return (
    <VStack
      spacing={0}
      className={style.icontabs}
      data-testid="panel-tabs"
      data-active-tab={String(state.selectedItem?.key)}
      {...dataAttributes}
    >
      <HStack spacing={0} domProps={tabListProps} ref={ref}>
        {[...state.collection].map((item) => (
          <Tab
            key={item.key}
            item={item}
            state={state}
            data-testid={`${dataAttributes["data-testid"] ?? "panel-tabs"}__tab__${item.key}`}
          />
        ))}
        {props.tabsActions != null && (
          <>
            <Spacer />
            {props.tabsActions}
          </>
        )}
      </HStack>
      <TabPanel
        key={state.selectedItem?.key}
        state={state}
        data-testid={`${dataAttributes["data-testid"] ?? "panel-tabs"}__panel`}
        data-current-tab={String(state.selectedItem?.key)}
      />
    </VStack>
  );
};

const Tab: FC<{ item: Node<TabType>; state: TabListState<TabType> }> = ({ item, state, ...props }) => {
  const triggerRef = useRef(null);
  const { tabProps } = useTab(item, state, triggerRef);

  const { triggerProps, tooltipProps, state: tooltipState } = useTooltip(triggerRef, { closeDelay: 0 });

  const dataAttributes = useDataAttributes(props);

  return (
    <>
      <div {...mergeProps(tabProps, triggerProps)} ref={triggerRef} {...dataAttributes}>
        {item.rendered}
      </div>
      <AnimatedTooltip
        title={item.textValue}
        placement="top"
        offset={0}
        domProps={tooltipProps}
        state={tooltipState}
        targetRef={triggerRef}
        nonInteractive
      />
    </>
  );
};

const TabPanel: FC<AriaTabPanelProps & { state: TabListState<TabType> }> = ({ state, ...props }) => {
  const ref = useRef(null);
  const { tabPanelProps } = useTabPanel(props, state, ref);

  const dataAttributes = useDataAttributes(props);

  return (
    <VStack spacing={0} domProps={tabPanelProps} ref={ref} className={style.panel} {...dataAttributes}>
      {state.selectedItem?.props.children}
    </VStack>
  );
};
